import _, { has, get, union, snakeCase } from 'lodash';
import type {
  Model,
  Kind,
  Attribute,
  RelationalAttribute,
  ComponentAttribute,
  DynamicZoneAttribute,
  WithRequired,
} from './types';

const SINGLE_TYPE = 'singleType';
const COLLECTION_TYPE = 'collectionType';

const ID_ATTRIBUTE = 'id';
const DOC_ID_ATTRIBUTE = 'documentId';

const PUBLISHED_AT_ATTRIBUTE = 'publishedAt';
const FIRST_PUBLISHED_AT_ATTRIBUTE = 'firstPublishedAt';
const CREATED_BY_ATTRIBUTE = 'createdBy';
const UPDATED_BY_ATTRIBUTE = 'updatedBy';

const CREATED_AT_ATTRIBUTE = 'createdAt';
const UPDATED_AT_ATTRIBUTE = 'updatedAt';

const constants = {
  ID_ATTRIBUTE,
  DOC_ID_ATTRIBUTE,
  PUBLISHED_AT_ATTRIBUTE,
  FIRST_PUBLISHED_AT_ATTRIBUTE,
  CREATED_BY_ATTRIBUTE,
  UPDATED_BY_ATTRIBUTE,
  CREATED_AT_ATTRIBUTE,
  UPDATED_AT_ATTRIBUTE,
  SINGLE_TYPE,
  COLLECTION_TYPE,
};

/** ID-like fields accepted at root level and on relations/media/components (validate/sanitize traversal). */
const ID_FIELDS: string[] = [ID_ATTRIBUTE, DOC_ID_ATTRIBUTE];
/** Keys accepted on morphTo relation payloads (e.g. __type). */
const MORPH_TO_KEYS: string[] = ['__type'];
/** Keys accepted on dynamic zone component payloads (e.g. __component). */
const DYNAMIC_ZONE_KEYS: string[] = ['__component'];
/** Relation operation keys (connect, disconnect, set, options). */
const RELATION_OPERATION_KEYS: string[] = ['connect', 'disconnect', 'set', 'options'];

/**
 * Reserved attribute names that cannot be used by user-defined content-type fields.
 * Entries ending with `*` are treated as prefix matchers (e.g. `strapi*` blocks `strapi_foo`).
 */
// use snake_case
const RESERVED_ATTRIBUTE_NAMES: string[] = [
  // ID fields
  'id',
  'document_id',

  // Creator fields
  'created_at',
  'updated_at',
  'published_at',
  // V6: we will need to add first_published_at when it becomes the default behaviour
  'created_by_id',
  'updated_by_id',
  // does not actually conflict because the fields are called *_by_id but we'll leave it to avoid confusion
  'created_by',
  'updated_by',

  // Used for Strapi functionality
  'entry_id',
  'localizations',
  'meta',
  'locale',
  '__component',
  '__contentType',

  // We support ending with * to denote prefixes
  'strapi*',
  '_strapi*',
  '__strapi*',
];

/**
 * Reserved attribute names that only conflict when draftAndPublish is enabled.
 * `status` is the v5 Document Service / REST query parameter for draft/published filtering.
 */
// use snake_case
const RESERVED_ATTRIBUTE_NAMES_DRAFT_PUBLISH: string[] = ['status'];

/** Reserved model (collection) names that cannot be used by user-defined content-types. */
// use snake_case
const RESERVED_MODEL_NAMES: string[] = [
  'boolean',
  'date',
  'date_time',
  'time',
  'upload',
  'document',
  'then', // no longer an issue but still restricting for being a javascript keyword

  // We support ending with * to denote prefixes
  'strapi*',
  '_strapi*',
  '__strapi*',
];

const matchesReservedName = (snakeCaseName: string, list: string[]): boolean => {
  if (list.includes(snakeCaseName)) {
    return true;
  }

  return list
    .filter((entry) => entry.endsWith('*'))
    .map((entry) => entry.slice(0, -1))
    .some((prefix) => snakeCaseName.startsWith(prefix));
};

interface IsReservedAttributeNameOptions {
  /** When true, draft-and-publish-specific names (e.g. `status`) are also reserved. Defaults to true. */
  draftAndPublish?: boolean;
}

// compare snake case to check the actual column names that will be used in the database
const isReservedAttributeName = (
  name: string,
  { draftAndPublish = true }: IsReservedAttributeNameOptions = {}
): boolean => {
  const snakeCaseName = snakeCase(name);

  if (matchesReservedName(snakeCaseName, RESERVED_ATTRIBUTE_NAMES)) {
    return true;
  }

  if (
    draftAndPublish &&
    matchesReservedName(snakeCaseName, RESERVED_ATTRIBUTE_NAMES_DRAFT_PUBLISH)
  ) {
    return true;
  }

  return false;
};

// compare snake case to check the actual column names that will be used in the database
const isReservedModelName = (name: string): boolean => {
  return matchesReservedName(snakeCase(name), RESERVED_MODEL_NAMES);
};

const getReservedAttributeNames = ({
  draftAndPublish = true,
}: IsReservedAttributeNameOptions = {}): string[] => {
  return draftAndPublish
    ? [...RESERVED_ATTRIBUTE_NAMES, ...RESERVED_ATTRIBUTE_NAMES_DRAFT_PUBLISH]
    : [...RESERVED_ATTRIBUTE_NAMES];
};

const getReservedModelNames = (): string[] => [...RESERVED_MODEL_NAMES];

const findDraftAndPublishReservedAttributeNames = (attributeNames: Iterable<string>): string[] => {
  return [...attributeNames].filter((name) =>
    matchesReservedName(snakeCase(name), RESERVED_ATTRIBUTE_NAMES_DRAFT_PUBLISH)
  );
};

const getDraftAndPublishReservedAttributeWarning = (uid: string, attributeName: string): string =>
  `The attribute name '${attributeName}' on content type '${uid}' is reserved when 'draftAndPublish' is enabled. It conflicts with the Document Service / REST 'status' query parameter. Rename the attribute or disable the 'draftAndPublish' option.`;

const getDraftAndPublishEnableBlockedMessage = (attributeNames: string[]): string =>
  `Cannot enable draft and publish while the following attribute names are reserved: ${attributeNames.join(', ')}. Rename them or remove them first.`;

const getTimestamps = (model: Model) => {
  const attributes: string[] = [];

  if (has(model.attributes, CREATED_AT_ATTRIBUTE)) {
    attributes.push(CREATED_AT_ATTRIBUTE);
  }

  if (has(model.attributes, UPDATED_AT_ATTRIBUTE)) {
    attributes.push(UPDATED_AT_ATTRIBUTE);
  }

  return attributes;
};

const getCreatorFields = (model: Model) => {
  const attributes: string[] = [];

  if (has(model.attributes, CREATED_BY_ATTRIBUTE)) {
    attributes.push(CREATED_BY_ATTRIBUTE);
  }

  if (has(model.attributes, UPDATED_BY_ATTRIBUTE)) {
    attributes.push(UPDATED_BY_ATTRIBUTE);
  }

  return attributes;
};

const getNonWritableAttributes = (model: Model) => {
  if (!model) return [];

  const nonWritableAttributes = _.reduce(
    model.attributes,
    (acc, attr, attrName) => (attr.writable === false ? acc.concat(attrName) : acc),
    [] as string[]
  );

  return _.uniq([
    ID_ATTRIBUTE,
    DOC_ID_ATTRIBUTE,
    ...getTimestamps(model),
    ...nonWritableAttributes,
  ]);
};

const getWritableAttributes = (model: Model) => {
  if (!model) return [];

  return _.difference(Object.keys(model.attributes), getNonWritableAttributes(model));
};

const isWritableAttribute = (model: Model, attributeName: string) => {
  return getWritableAttributes(model).includes(attributeName);
};

const getNonVisibleAttributes = (model: Model) => {
  const nonVisibleAttributes = _.reduce(
    model.attributes,
    (acc, attr, attrName) => (attr.visible === false ? acc.concat(attrName) : acc),
    [] as string[]
  );

  return _.uniq([
    ID_ATTRIBUTE,
    DOC_ID_ATTRIBUTE,
    PUBLISHED_AT_ATTRIBUTE,
    ...getTimestamps(model),
    ...nonVisibleAttributes,
  ]);
};

const getVisibleAttributes = (model: Model) => {
  return _.difference(_.keys(model.attributes), getNonVisibleAttributes(model));
};

const isVisibleAttribute = (model: Model, attributeName: string) => {
  return getVisibleAttributes(model).includes(attributeName);
};

const getOptions = (model: Model) =>
  _.assign({ draftAndPublish: false }, _.get(model, 'options', {}));

const hasDraftAndPublish = (model: Model) =>
  _.get(model, 'options.draftAndPublish', false) === true;

const hasFirstPublishedAtField = (model: Model) =>
  strapi.config.get('features.future.experimental_firstPublishedAt', false) &&
  hasDraftAndPublish(model);

const isDraft = <T extends object>(data: T, model: Model) =>
  hasDraftAndPublish(model) && _.get(data, PUBLISHED_AT_ATTRIBUTE) === null;

const isSchema = (data: unknown): data is Model => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'modelType' in data &&
    typeof data.modelType === 'string' &&
    ['component', 'contentType'].includes(data.modelType)
  );
};

const isComponentSchema = (data: unknown): data is Model & { modelType: 'component' } => {
  return isSchema(data) && data.modelType === 'component';
};

const isContentTypeSchema = (data: unknown): data is Model & { modelType: 'contentType' } => {
  return isSchema(data) && data.modelType === 'contentType';
};

const isSingleType = ({ kind = COLLECTION_TYPE }) => kind === SINGLE_TYPE;
const isCollectionType = ({ kind = COLLECTION_TYPE }) => kind === COLLECTION_TYPE;
const isKind = (kind: Kind) => (model: Model) => model.kind === kind;

/**
 * Memo of each model's "stored" private attributes — those declared in
 * `api.responses.privateAttributes` config or in the model's `options.privateAttributes`,
 * as opposed to attributes flagged `private: true` individually.
 *
 * `isPrivateAttribute` is called once per key, per node, per traversal, per entity, and
 * recomputing this set every time dominated response sanitization: a 25-entity page made
 * 48,605 calls, each doing two lodash path lookups plus a fresh `union()` allocation, all
 * returning the same answer.
 *
 * Keyed on the model object rather than its uid, so a rebuilt schema is a distinct key and
 * the entry cannot go stale. Deliberately NOT stored on the schema itself: a
 * `privateAttributes` getter used to live there and was removed in 2023 (2fa8f30371)
 * because it polluted schema serialization, and because it was only ever attached by
 * `createContentType`, so components never got one.
 *
 * Both inputs are fixed for a model's lifetime — `api.responses.privateAttributes` is
 * boot configuration and is never written at runtime, and `options.privateAttributes`
 * comes from the schema definition. The cache is only populated once `strapi.config` is
 * available so that a lookup during early boot cannot record an empty set permanently.
 */
const storedPrivateAttributesCache = new WeakMap<object, Set<string>>();

const computeStoredPrivateAttributes = (model: Model): Set<string> =>
  new Set(
    union(
      get(model, 'options.privateAttributes', []) as Array<string>,
      (strapi?.config?.get('api.responses.privateAttributes', []) ?? []) as Array<string>
    )
  );

const getStoredPrivateAttributesSet = (model: Model): Set<string> => {
  const cacheable = typeof model === 'object' && model !== null && strapi?.config != null;

  if (!cacheable) {
    return computeStoredPrivateAttributes(model);
  }

  let cached = storedPrivateAttributesCache.get(model as object);

  if (cached === undefined) {
    cached = computeStoredPrivateAttributes(model);
    storedPrivateAttributesCache.set(model as object, cached);
  }

  return cached;
};

// Set iteration order is insertion order, which matches what `union` returned before.
const getStoredPrivateAttributes = (model: Model) =>
  Array.from(getStoredPrivateAttributesSet(model));

const getPrivateAttributes = (model: Model) => {
  return _.union(
    getStoredPrivateAttributes(model),
    _.keys(_.pickBy(model.attributes, (attr) => !!attr.private))
  );
};

const isPrivateAttribute = (model: Model, attributeName: string) => {
  if (model?.attributes?.[attributeName]?.private === true) {
    return true;
  }

  const storedPrivateAttributes = getStoredPrivateAttributesSet(model);

  // The set is empty for the overwhelming majority of models, so check size before hashing.
  return storedPrivateAttributes.size !== 0 && storedPrivateAttributes.has(attributeName);
};

const isScalarAttribute = (attribute?: Attribute) => {
  return attribute && !['media', 'component', 'relation', 'dynamiczone'].includes(attribute.type);
};

const getDoesAttributeRequireValidation = (attribute: Attribute) => {
  return (
    attribute.required ||
    attribute.unique ||
    Object.prototype.hasOwnProperty.call(attribute, 'max') ||
    Object.prototype.hasOwnProperty.call(attribute, 'min') ||
    Object.prototype.hasOwnProperty.call(attribute, 'maxLength') ||
    Object.prototype.hasOwnProperty.call(attribute, 'minLength')
  );
};
const isMediaAttribute = (attribute?: Attribute) => attribute?.type === 'media';
const isRelationalAttribute = (attribute?: Attribute): attribute is RelationalAttribute =>
  attribute?.type === 'relation';

const HAS_RELATION_REORDERING = ['manyToMany', 'manyToOne', 'oneToMany'];
const hasRelationReordering = (attribute?: Attribute) =>
  isRelationalAttribute(attribute) && HAS_RELATION_REORDERING.includes(attribute.relation);

const isComponentAttribute = (
  attribute?: Attribute
): attribute is ComponentAttribute | DynamicZoneAttribute =>
  !!attribute && ['component', 'dynamiczone'].includes(attribute.type);

const isDynamicZoneAttribute = (attribute?: Attribute): attribute is DynamicZoneAttribute =>
  !!attribute && attribute.type === 'dynamiczone';
const isMorphToRelationalAttribute = (attribute?: Attribute) => {
  return (
    !!attribute && isRelationalAttribute(attribute) && attribute.relation?.startsWith?.('morphTo')
  );
};

const getComponentAttributes = (schema: Model) => {
  return _.reduce(
    schema.attributes,
    (acc, attr, attrName) => {
      if (isComponentAttribute(attr)) acc.push(attrName);
      return acc;
    },
    [] as string[]
  );
};

const getMediaAttributes = (schema: Model) => {
  return _.reduce(
    schema.attributes,
    (acc, attr, attrName) => {
      if (isMediaAttribute(attr)) acc.push(attrName);
      return acc;
    },
    [] as string[]
  );
};

const getScalarAttributes = (schema: Model) => {
  return _.reduce(
    schema.attributes,
    (acc, attr, attrName) => {
      if (isScalarAttribute(attr)) acc.push(attrName);
      return acc;
    },
    [] as string[]
  );
};

const getRelationalAttributes = (schema: Model) => {
  return _.reduce(
    schema.attributes,
    (acc, attr, attrName) => {
      if (isRelationalAttribute(attr)) acc.push(attrName);
      return acc;
    },
    [] as string[]
  );
};

/**
 * Checks if an attribute is of type `type`
 * @param {object} attribute
 * @param {string} type
 */
const isTypedAttribute = (attribute: Attribute, type: string) => {
  return _.has(attribute, 'type') && attribute.type === type;
};

/**
 *  Returns a route prefix for a contentType
 * @param {object} contentType
 * @returns {string}
 */
const getContentTypeRoutePrefix = (contentType: WithRequired<Model, 'info'>) => {
  return isSingleType(contentType)
    ? _.kebabCase(contentType.info.singularName)
    : _.kebabCase(contentType.info.pluralName);
};

export {
  isSchema,
  isContentTypeSchema,
  isComponentSchema,
  isScalarAttribute,
  isMediaAttribute,
  isRelationalAttribute,
  hasRelationReordering,
  isComponentAttribute,
  isDynamicZoneAttribute,
  isMorphToRelationalAttribute,
  isTypedAttribute,
  getPrivateAttributes,
  isPrivateAttribute,
  constants,
  ID_FIELDS,
  MORPH_TO_KEYS,
  DYNAMIC_ZONE_KEYS,
  RELATION_OPERATION_KEYS,
  RESERVED_ATTRIBUTE_NAMES,
  RESERVED_ATTRIBUTE_NAMES_DRAFT_PUBLISH,
  RESERVED_MODEL_NAMES,
  isReservedAttributeName,
  isReservedModelName,
  getReservedAttributeNames,
  getReservedModelNames,
  findDraftAndPublishReservedAttributeNames,
  getDraftAndPublishReservedAttributeWarning,
  getDraftAndPublishEnableBlockedMessage,
  getNonWritableAttributes,
  getComponentAttributes,
  getMediaAttributes,
  getScalarAttributes,
  getRelationalAttributes,
  getWritableAttributes,
  isWritableAttribute,
  getNonVisibleAttributes,
  getVisibleAttributes,
  getTimestamps,
  getCreatorFields,
  isVisibleAttribute,
  getOptions,
  isDraft,
  hasDraftAndPublish,
  hasFirstPublishedAtField,
  isSingleType,
  isCollectionType,
  isKind,
  getContentTypeRoutePrefix,
  getDoesAttributeRequireValidation,
};
